import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TRANSLATIONS } from './translations';

export type AppLocale = 'en' | 'ar' | 'ur' | 'es' | 'zh' | 'pa';

const SUPPORTED_LOCALES: AppLocale[] = ['en', 'ar', 'ur', 'es', 'zh', 'pa'];
const RTL_LOCALES: AppLocale[] = ['ar', 'ur'];

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private currentLocale = new BehaviorSubject<AppLocale>(this.getInitialLocale());
  locale$ = this.currentLocale.asObservable();

  constructor() {
    this.updateHtmlDirection(this.currentLocale.value);
  }

  private getInitialLocale(): AppLocale {
    const saved = localStorage.getItem('appLocale') as AppLocale;
    if (SUPPORTED_LOCALES.includes(saved)) {
      return saved;
    }
    const browserLang = (navigator.language || 'en').toLowerCase();
    if (browserLang.startsWith('ar')) return 'ar';
    if (browserLang.startsWith('ur')) return 'ur';
    if (browserLang.startsWith('es')) return 'es';
    if (browserLang.startsWith('zh')) return 'zh';
    if (browserLang.startsWith('pa')) return 'pa';
    return 'en';
  }

  getLocale(): AppLocale {
    return this.currentLocale.value;
  }

  setLocale(locale: string) {
    if (SUPPORTED_LOCALES.includes(locale as AppLocale)) {
      const next = locale as AppLocale;
      localStorage.setItem('appLocale', next);
      this.currentLocale.next(next);
      this.updateHtmlDirection(next);
    }
  }

  getTranslation(key: string): string {
    const locale = this.currentLocale.value;
    if (TRANSLATIONS[locale] && TRANSLATIONS[locale][key]) {
      return TRANSLATIONS[locale][key];
    }
    if (TRANSLATIONS['en'] && TRANSLATIONS['en'][key]) {
      return TRANSLATIONS['en'][key];
    }
    return '';
  }

  private updateHtmlDirection(locale: AppLocale) {
    if (typeof document !== 'undefined') {
      const html = document.documentElement;
      if (RTL_LOCALES.includes(locale)) {
        html.setAttribute('dir', 'rtl');
        html.setAttribute('lang', locale);
      } else {
        html.setAttribute('dir', 'ltr');
        html.setAttribute('lang', locale === 'en' ? 'en' : locale);
      }
    }
  }
}
